import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// Import Types
import type { DynamicVariableChannelData } from 'types';
// Import Hooks
import useBind from 'hooks/UseBind';
import useInputInsertion from 'hooks/UseInputInsertion';
// Import UI Components
import { Section } from '@geneui/components';
// Import Components
import SMSContent from './SMSContent';
import DynamicVariables from './DynamicVariables';
import TemplateCategoryInput from './TemplateCategoryInput';
import WhatsAppProviderTemplateContent from './WhatsAppProviderTemplateContent';
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import { l, TemplateInputTypes } from 'constants/common';
import { DefaultLanguage, IS_WHATSAPP_CASCADE_ENABLED } from 'constants/template';

interface Props {
    templateData: AnyObject;
    templateLangKey: string;
    bodyValidationState?: AnyObject;
    maxSMSPartsCount: number;
    shortenerAndTrackerSettings: AnyObject;
    dynamicVariables: DynamicVariableChannelData[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleChange: (propertyName: string, value: any, changeTimeout?: number) => void;
}

const WhatsAppProviderCascadeContent: React.FC<Props> = (props) => {
    const {
        templateData,
        templateLangKey,
        maxSMSPartsCount,
        shortenerAndTrackerSettings,
        dynamicVariables,
        handleChange,
    } = props;
    const { t } = useTranslation();

    const { insertionHandler, updateInsertionHandler, insertionOptions, setInsertionOptions } = useInputInsertion();

    const categoryValue = templateData?.[TemplateInputTypes.Category];

    const handleCategoryChange = useBind(handleChange, TemplateInputTypes.Category);
    const updateDynamicVariableOptionsWhatsAppProvider = useCallback(
        ({ hintText, focusedInputKey }) => {
            setInsertionOptions({
                channel: TemplateTypeEnum.WhatsAppProvider,
                inputFilterKey: '',
                hintText,
                focusedInputKey,
            });
        },
        [setInsertionOptions],
    );

    const updateDynamicVariableOptionsSMS = useCallback(() => {
        setInsertionOptions({
            channel: TemplateTypeEnum.SMS,
            inputFilterKey: '',
            hintText: '',
            focusedInputKey: TemplateInputTypes.SMSBody,
        });
    }, [setInsertionOptions]);

    return (
        <>
            <DynamicVariables
                activeChannel=""
                dynamicVariables={dynamicVariables}
                clickHandler={insertionHandler}
                insertionOptions={insertionOptions}
            />
            <div className="crm-create-template-page-c-c-h-inputs-row">
                {templateLangKey === DefaultLanguage && (
                    <TemplateCategoryInput value={categoryValue} onChange={handleCategoryChange} />
                )}
            </div>

            {!IS_WHATSAPP_CASCADE_ENABLED ? (
                <WhatsAppProviderTemplateContent
                    templateData={templateData}
                    templateLangKey={templateLangKey}
                    dynamicVariables={dynamicVariables}
                    handleChange={handleChange}
                    shortenerAndTrackerSettings={shortenerAndTrackerSettings.whatsAppProvider}
                    updateDynamicVariableOptions={updateDynamicVariableOptionsWhatsAppProvider}
                    updateInsertionHandler={updateInsertionHandler}
                />
            ) : (
                <div className="viber-template-cascade-container">
                    <div className="viber-template-cascade-section viber-content">
                        <Section text={t(l.ViberContent)}>
                            <div className="viber-template-cascade-section-content">
                                <WhatsAppProviderTemplateContent
                                    templateData={templateData}
                                    templateLangKey={templateLangKey}
                                    dynamicVariables={dynamicVariables}
                                    handleChange={handleChange}
                                    shortenerAndTrackerSettings={shortenerAndTrackerSettings.whatsAppProvider}
                                    updateDynamicVariableOptions={updateDynamicVariableOptionsWhatsAppProvider}
                                    updateInsertionHandler={updateInsertionHandler}
                                />
                            </div>
                        </Section>
                    </div>
                    <div className="viber-template-cascade-section sms-content">
                        <Section text={t(l.SMSContent)}>
                            <div className="viber-template-cascade-section-content">
                                <SMSContent
                                    bodyPropertyName={TemplateInputTypes.SMSBody}
                                    templateData={templateData}
                                    templateLangKey={templateLangKey}
                                    maxSMSPartsCount={maxSMSPartsCount}
                                    handleChange={handleChange}
                                    shortenerAndTrackerSettings={shortenerAndTrackerSettings.sms}
                                    updateDynamicVariableOptions={updateDynamicVariableOptionsSMS}
                                    updateInsertionHandler={updateInsertionHandler}
                                    shouldFocusOnMount={false}
                                />
                            </div>
                        </Section>
                    </div>
                </div>
            )}
        </>
    );
};

export default memo(WhatsAppProviderCascadeContent);
